import type { InventoryItem, ItemAffix } from '../net/protocol';

/**
 * An item's colour, from what its affixes actually do.
 *
 * Asked for directly: *can gear icons be colour tinted depending on some prefixes or suffixes … it
 * would create diversity.*
 *
 * ── Why the affix and not the rarity ─────────────────────────────────────────
 *
 * A first attempt at this put a rarity-coloured glow around the icon and was rejected on sight, for
 * a good reason: **rarity is already on the frame.** `.grid__item--rare` colours the border and the
 * name, so a second rarity signal spends a whole visual channel repeating something the player can
 * already read, and spends it on the one thing that would otherwise be free to say something new.
 *
 * An affix is what is actually different between two rare swords. Two Rares side by side are the
 * same yellow today; one rolled fire damage and the other rolled cold, and *that* is the diversity
 * worth drawing. It also means the tint is information rather than decoration — a red weapon is a
 * fire weapon, every time, because the colour is derived from the stat key rather than assigned.
 *
 * ── Why not random ───────────────────────────────────────────────────────────
 *
 * The ask allowed "even just randomly". Random was not taken. A colour that means nothing still
 * *looks* like it means something, so a player would spend the first hour trying to learn it — and
 * there is no seed on the wire to derive it from that survives an item being re-sent, so two clients
 * would disagree about what colour a dropped sword is.
 */

/** The families an affix can put on an item. Closed, and each is a hue a player can name. */
export type TintFamily =
  | 'fire'
  | 'cold'
  | 'lightning'
  | 'void'
  | 'poison'
  | 'physical'
  | 'vitality'
  | 'arcane'
  | 'agility'
  | 'defence';

/**
 * Target hues, in degrees. Chosen to be nameable and, more importantly, **separable**: adjacent
 * entries are at least 30° apart, because two tints a player cannot tell apart are worse than one
 * tint, and this is the same reasoning `ART-DIRECTION-UI-SYSTEM.md` applies to the rarity colours in
 * ΔE.
 *
 * The elemental five match what the spell effects already use (`fx.ts`'s elements), so a fire
 * weapon and a fire bolt are the same red. That agreement is worth more than a prettier palette.
 */
export const TINT_HUE: Record<TintFamily, number> = {
  fire: 12,
  poison: 95,
  cold: 195,
  lightning: 52,
  void: 280,
  arcane: 250,
  physical: 25,
  vitality: 340,
  agility: 130,
  defence: 210,
};

/**
 * Which family a stat key belongs to.
 *
 * Every key here is from `config.StatKeys` — the vocabulary the loader validates — so a typo is a
 * key that never matches rather than a key that means something else. An **unmapped** key is normal
 * and deliberate: `cooldown_sec`, `mana_cost` and `potion_capacity` describe an item's economy
 * rather than its character, and tinting on those would colour items by something no player thinks
 * of as their identity.
 */
const FAMILY_BY_STAT: Record<string, TintFamily> = {
  fire_damage: 'fire',
  fire_res_pct: 'fire',
  cold_damage: 'cold',
  cold_res_pct: 'cold',
  lightning_damage: 'lightning',
  lightning_res_pct: 'lightning',
  void_damage: 'void',

  physical_damage_flat: 'physical',
  physical_damage_pct: 'physical',
  damage: 'physical',
  damage_min: 'physical',
  damage_max: 'physical',
  crit_chance_pct: 'physical',
  crit_damage_pct: 'physical',
  strength: 'physical',

  health_flat: 'vitality',
  health_regen_flat: 'vitality',
  hp: 'vitality',
  endurance: 'vitality',

  intelligence: 'arcane',
  mana_flat: 'arcane',
  mana_regen_flat: 'arcane',
  spell_damage_pct: 'arcane',
  spell_crit_chance_pct: 'arcane',

  dexterity: 'agility',
  evasion: 'agility',
  move_speed_pct: 'agility',
  attack_speed: 'agility',
  attack_speed_pct: 'agility',

  armor: 'defence',
  armor_flat: 'defence',
  block_chance_pct: 'defence',
  all_res_pct: 'defence',
  reduction: 'defence',
};

/** The family an affix implies, or null when its stat is not one this tints on. */
export function familyOf(affix: ItemAffix): TintFamily | null {
  return FAMILY_BY_STAT[affix.stat] ?? null;
}

export interface ItemTint {
  family: TintFamily;
  /** The target hue in degrees, for the CSS filter chain. */
  hue: number;
  /** Which affix decided it, so a tooltip or a test can say why. */
  because: ItemAffix;
}

/**
 * The tint an item wears, or null when nothing it rolled maps to a family.
 *
 * **Prefix before suffix, then best tier.** A prefix is the adjective in front of the name — the
 * *kind* of thing it is (Heavy, Frozen, Blazing) — and a suffix is what it also happens to do, so
 * the prefix is the better claim on the item's colour. Within a slot the best roll wins, because
 * that is the affix the player kept the item for. `tier` counts **down** to quality: 1 is the best
 * and 10 the weakest, and 0 is a unique's fixed roll, which is better than any of them.
 *
 * Null for an unaffixed Normal item, which is most of a bag — and that is the feature rather than a
 * gap. A tint on everything is a tint on nothing; leaving three quarters of the grid untinted is
 * what makes the tinted ones legible at a glance.
 */
export function affixTint(item: InventoryItem): ItemTint | null {
  // **A unique is never tinted.** It has art of its own — the four unique sheets are 100 icons
  // painted per item — so it is already the most identifiable thing in the bag, and recolouring it
  // would throw away the one picture that was drawn for that item specifically. Owner's call.
  if (item.rarity === 'UNIQUE') return null;

  const ranked = [...item.affixes]
    .map((affix) => ({ affix, family: familyOf(affix) }))
    .filter((entry): entry is { affix: ItemAffix; family: TintFamily } => entry.family !== null)
    .sort((a, b) => {
      const slot = slotRank(a.affix) - slotRank(b.affix);
      if (slot !== 0) return slot;
      return tierRank(a.affix) - tierRank(b.affix);
    });

  const best = ranked[0];
  if (!best) return null;
  return { family: best.family, hue: TINT_HUE[best.family], because: best.affix };
}

/** Prefixes sort first. */
function slotRank(affix: ItemAffix): number {
  return affix.slot === 'PREFIX' ? 0 : 1;
}

/**
 * Sortable quality: lower is better.
 *
 * `tier` is already "1 is best", with **0 meaning a unique's fixed roll** — which has no tier and is
 * not a weak one. Mapping 0 to −1 keeps a single ascending sort instead of a special case at every
 * comparison, which is the shape of bug `tier` invites.
 */
function tierRank(affix: ItemAffix): number {
  return affix.tier === 0 ? -1 : affix.tier;
}

/**
 * How far the CSS filter chain has to rotate to reach a hue.
 *
 * The chain desaturates the art, pushes it through `sepia()` to put chroma back into what were
 * greys, and then rotates that to the target. **`sepia()` lands at about 38°** — a warm brown — so
 * every rotation is measured from there rather than from zero. Getting this wrong is not a crash: it
 * is every tint being the same amount wrong, which reads as a palette choice.
 */
export const SEPIA_BASE_HUE = 38;

export function tintRotation(hue: number): number {
  return Math.round(hue - SEPIA_BASE_HUE);
}
